import { enqueueSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';

import { Box } from '@mui/system';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { LoadingButton } from '@mui/lab';
import Grid from '@mui/system/Unstable_Grid';
import { Card, Link, Stack, MenuItem, CardHeader, IconButton, Typography, CardContent } from '@mui/material';

import { paths } from 'src/routes/paths';
import { useParams, useRouter } from 'src/routes/hooks';

import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useCustomFields } from 'src/hooks/use-custom-fields';

import { fDate } from 'src/utils/format-time';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { FeeType } from 'src/services/jobs/jobs.types';
import { openPlacementEditor } from 'src/store/slices/jobs/jobsSlice';
import { CustomFieldEntityType } from 'src/services/custom-fields/custom-field.types';
import { useGetPlacementByIdQuery, useRemovePlacementMutation } from 'src/services/applications/applications.service';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import ListSkeleton from 'src/components/list/list-skeleton';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

import { TenantType } from 'src/types/enums';

import PlacementDocumentsView from './placement-documents';

// ----------------------------------------------------------------------

enum PlacementTab {
  DETAIL = 'detail',
  DOCUMENTS = 'documents',
}

export default function PlacementDetailView() {
  const { t } = useTranslate();

  const dispatch = useAppDispatch();

  const { placement_id } = useParams();

  const router = useRouter();

  const confirmDelete = useBoolean();

  const popover = usePopover();

  const [currentTab, setCurrentTab] = useState<PlacementTab | undefined>();

  const [searchParams, setSearchParams] = useSearchParams();

  const tenantType = useOrgTenant();

  const [removePlacement, { isLoading: isDeletingPlacement }] = useRemovePlacementMutation();

  const customFields = useCustomFields({
    entityType: CustomFieldEntityType.PLACEMENT,
    entityId: placement_id as string,
  });

  const { data: placement, isLoading: isLoadingPlacement } = useGetPlacementByIdQuery(
    placement_id as string,
    {
      skip: !placement_id,
    }
  );

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (currentTab) return;

    let defaultTab: PlacementTab = PlacementTab.DETAIL;

    const existingTab = searchParams.get('tab');
    if (existingTab && Object.values(PlacementTab).includes(existingTab as PlacementTab)) {
      defaultTab = existingTab as PlacementTab;
    }

    setCurrentTab(defaultTab);
  }, [currentTab, searchParams]);

  const handleChangeTab = useCallback((_event: React.SyntheticEvent, newValue: PlacementTab) => {
    setCurrentTab(newValue);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (!currentTab) return;

    setSearchParams({ "tab": currentTab }, { replace: true });
  }, [currentTab, setSearchParams]);

  const onDeletePlacement = async () => {
    if (!placement_id) return;

    try {
      await removePlacement(placement_id).unwrap();

      confirmDelete.onFalse();

      enqueueSnackbar(t('jobs.detail.placements.api.remove.success'), { variant: 'success' });

      // Navigate back to job detail page
      if (placement?.job?.id) {
        router.push(paths.dashboard.jobs.detail(placement.job.id));
      } else {
        router.push(paths.dashboard.jobs.root);
      }
    } catch (e) {
      enqueueSnackbar(t('jobs.detail.placements.api.remove.default_error'), { variant: 'error' });
    }
  };

  const navigateToJob = useCallback(() => {
    if (placement?.job?.id) {
      router.push(paths.dashboard.jobs.detail(placement.job.id));
    }
  }, [placement?.job?.id, router]);

  const navigateToCandidate = useCallback(() => {
    if (placement?.org_candidate?.id) {
      router.push(paths.dashboard.candidates.detail(placement.org_candidate.id));
    }
  }, [placement?.org_candidate?.id, router]);

  const navigateToApplication = useCallback(() => {
    if (placement?.application?.id && placement?.job?.id) {
      router.push(paths.dashboard.jobs.application(placement.job.id, placement.application.id));
    }
  }, [placement?.application?.id, placement?.job?.id, router]);

  const navigateToEmployerCompany = useCallback(() => {
    if (placement?.employer_company?.id) {
      router.push(paths.dashboard.companies.detail(
        placement.employer_company.id,
        TenantType.Client,
        TenantType.Client
      ));
    }
  }, [placement?.employer_company?.id, router]);

  const navigateToRecruiterCompany = useCallback(() => {
    if (placement?.recruiter_company?.id) {
      router.push(paths.dashboard.companies.detail(
        placement.recruiter_company.id,
        TenantType.Recruiter,
        TenantType.Recruiter
      ));
    }
  }, [placement?.recruiter_company?.id, router]);

  const navigateToBillingContact = useCallback(() => {
    if (placement?.billing_contact?.id) {
      router.push(paths.dashboard.contacts.detail(placement.billing_contact.id));
    }
  }, [placement?.billing_contact?.id, router]);

  const handleOpenPlacementEditor = useCallback(() => {
    if (!placement) return;

    dispatch(openPlacementEditor({ placementId: placement.id }));
  }, [dispatch, placement]);

  const renderHead = (
    <Card sx={{ flexGrow: 0, boxShadow: 'unset', pb: 1 }}>
      <CardHeader
        sx={{ pl: 0, width: '100%' }}
        title={

          <Stack direction="column" justifyContent="start" alignItems="start">
            <TruncatedTextField
              text={placement ? `${placement.candidate_first_name} ${placement.candidate_last_name}` : "Loading..."}
              limit={40}
              mode='characters'
              variant="h3"
              gutterBottom={false}
              showMoreEnabled={false}
              sx={{ width: '100%', textTransform: 'capitalize', mb: 0 }}
            />

            <Stack direction="row" justifyContent="start" alignItems="center" spacing={1}>
              <Typography
                variant="subtitle2"
                gutterBottom={false}
                sx={{ textTransform: 'capitalize', color: 'text.secondary' }}
              >
                {placement ? placement.job_title : ""}
              </Typography>

              {
                placement?.employment_type && (
                  <Label variant="soft" mt={0.5}>{t(`enums.employment_type.${placement?.employment_type}`)}</Label>
                )
              }
            </Stack>

          </Stack>
        }
        action={
          <Stack direction="row" alignItems="center" mt={2}>
            <IconButton
              onClick={popover.onOpen}
            >
              <Iconify icon="eva:more-vertical-fill" color="black" />
            </IconButton>
          </Stack>
        }
      />

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 140 }}
      >
        <MenuItem
          onClick={() => {
            popover.onClose();
            handleOpenPlacementEditor();
          }}
        >
          <Iconify icon="eva:edit-fill" />
          {t('common.edit')}
        </MenuItem>

        <MenuItem
          onClick={() => {
            popover.onClose();
            confirmDelete.onTrue();
          }}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon="eva:trash-2-outline" />
          {t('common.delete')}
        </MenuItem>
      </CustomPopover>
    </Card>
  );

  const renderTabs = (
    <Tabs value={currentTab} onChange={handleChangeTab} sx={{ pl: 2, mb: 2 }}>
      <Tab
        key={PlacementTab.DETAIL}
        iconPosition="end"
        value={PlacementTab.DETAIL}
        label={t('deal_flow.detail.tabs.detail')}
      />

      <Tab
        key={PlacementTab.DOCUMENTS}
        iconPosition="end"
        value={PlacementTab.DOCUMENTS}
        label={t('deal_flow.detail.tabs.documents')}
      />
    </Tabs>
  );

  if (isLoadingPlacement) {
    return (
      <Grid container spacing={3}>
        <Grid xs={12} md={8}>
          <ListSkeleton />
        </Grid>
        <Grid xs={12} md={4}>
          <Stack direction="column" spacing={2}>
            <ListSkeleton />
            <ListSkeleton />
          </Stack>
        </Grid>
      </Grid>
    );
  }

  if (!placement) {
    return (
      <Card>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('jobs.detail.placements.not_found')}
          </Typography>
          <Typography variant="body2" color="text.disabled">
            {t('jobs.detail.placements.not_found_message')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Grid container>
        <Grid xs={12}>
          {renderHead}
        </Grid>
      </Grid>

      {renderTabs}

      {
        currentTab === PlacementTab.DETAIL && (
          <Grid container spacing={3}>
            <Grid xs={12} md={8}>
              <Stack direction="column" spacing={2}>
                {/* Candidate Information Card */}
                <Card>
                  <CardHeader title={t('jobs.detail.placements.form.candidate_info')} />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            {t('jobs.detail.placements.form.candidate_name')}
                          </Typography>
                          <Link
                            component="button"
                            variant="body1"
                            onClick={navigateToCandidate}
                            sx={{ textAlign: 'left' }}
                          >
                            {`${placement.candidate_first_name} ${placement.candidate_last_name}`}
                          </Link>
                        </Stack>
                      </Grid>

                      <Grid xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            {t('jobs.detail.placements.form.email')}
                          </Typography>
                          <Typography variant="body1">
                            {placement.candidate_email}
                          </Typography>
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Job Information Card */}
                <Card>
                  <CardHeader title={t('jobs.detail.placements.form.job_info')} />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            {t('jobs.detail.placements.form.job_title')}
                          </Typography>
                          <Link
                            component="button"
                            variant="body1"
                            onClick={navigateToJob}
                            sx={{ textAlign: 'left' }}
                          >
                            {placement.job_title}
                          </Link>
                        </Stack>
                      </Grid>

                      <Grid xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            {t('jobs.detail.placements.form.employment_type')}
                          </Typography>
                          <Typography variant="body1">
                            {t(`enums.employment_type.${placement.employment_type}`)}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            {t('jobs.detail.placements.form.application')}
                          </Typography>
                          <Link
                            component="button"
                            variant="body1"
                            onClick={navigateToApplication}
                            sx={{ textAlign: 'left' }}
                          >
                            {t('jobs.detail.placements.form.view_application')}
                          </Link>
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Company Information Card */}
                {tenantType === TenantType.Recruiter && (
                  <Card>
                    <CardHeader title={t('jobs.detail.placements.form.client_info')} />
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid xs={12} md={6}>
                          <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                              {t('jobs.detail.placements.form.employer_company_name')}
                            </Typography>
                            {placement.employer_company ? (
                              <Link
                                component="button"
                                variant="body1"
                                onClick={navigateToEmployerCompany}
                                sx={{ textAlign: 'left' }}
                              >
                                {placement.employer_company.company_name}
                              </Link>
                            ) : (
                              <Typography variant="body1">
                                {placement.employer_company_name || t('common.unknown')}
                              </Typography>
                            )}
                          </Stack>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                )}

                {tenantType === TenantType.Client && (
                  <Card>
                    <CardHeader title={t('jobs.detail.placements.form.recruiter_info')} />
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid xs={12} >
                          <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                              {t('jobs.detail.placements.form.recruiter_company_name')}
                            </Typography>
                            {placement.recruiter_company ? (
                              <Link
                                component="button"
                                variant="body1"
                                onClick={navigateToRecruiterCompany}
                                sx={{ textAlign: 'left' }}
                              >
                                {placement.recruiter_company.company_name}
                              </Link>
                            ) : (
                              <Typography variant="body1">
                                {placement.recruiter_company_name || t('common.unknown')}
                              </Typography>
                            )}
                          </Stack>
                        </Grid>
                        <Grid xs={12} md={6}>
                          <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                              {t('jobs.detail.placements.form.recruiter_first_name')}
                            </Typography>
                            <Typography variant="body1">
                              {placement.recruiter_first_name ? placement.recruiter_first_name : t('common.not_specified')}
                            </Typography>
                          </Stack>
                        </Grid>

                        <Grid xs={12} md={6}>
                          <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                              {t('jobs.detail.placements.form.recruiter_last_name')}
                            </Typography>
                            <Typography variant="body1">
                              {placement.recruiter_last_name ? placement.recruiter_last_name : t('common.not_specified')}
                            </Typography>
                          </Stack>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                )}

                {/* Offer Information Card */}
                <Card>
                  <CardHeader title={t('jobs.detail.placements.form.offer_information')} />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            {t('jobs.detail.placements.form.start_date')}
                          </Typography>
                          <Typography variant="body1">
                            {placement.start_date ? fDate(placement.start_date) : t('common.not_specified')}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            {t('jobs.detail.placements.form.end_date')}
                          </Typography>
                          <Typography variant="body1">
                            {placement.end_date ? fDate(placement.end_date) : t('common.not_specified')}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            {t('jobs.detail.placements.form.offer_accepted_at')}
                          </Typography>
                          <Typography variant="body1">
                            {placement.offer_accepted_at ? fDate(placement.offer_accepted_at) : t('common.not_specified')}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            {t('jobs.detail.placements.form.contract_sent_at')}
                          </Typography>
                          <Typography variant="body1">
                            {placement.contract_sent_at ? fDate(placement.contract_sent_at) : t('common.not_specified')}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            {t('jobs.detail.placements.form.contract_signed_at')}
                          </Typography>
                          <Typography variant="body1">
                            {placement.contract_signed_at ? fDate(placement.contract_signed_at) : t('common.not_specified')}
                          </Typography>
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Financial Information Card */}
                <Card>
                  <CardHeader title={t('jobs.detail.placements.form.financial_info')} />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            {t('jobs.detail.placements.form.base_salary')}
                          </Typography>
                          <Typography variant="body1">
                            {placement.base_salary ? `${placement.base_salary} ${placement.currency?.code || ''}` : t('common.not_specified')}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            {t('jobs.detail.placements.form.total_remuneration')}
                          </Typography>
                          <Typography variant="body1">
                            {placement.total_remuneration ? `${placement.total_remuneration} ${placement.currency?.code || ''}` : t('common.not_specified')}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            {t('jobs.detail.placements.form.additional_entitlements')}
                          </Typography>
                          <Typography variant="body1">
                            {placement.additional_entitlements ? `${placement.additional_entitlements} ${placement.currency?.code || ''}` : t('common.not_specified')}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            {t('jobs.detail.placements.form.pay_rate')}
                          </Typography>
                          <Typography variant="body1">
                            {placement.pay_rate ? `${t(`enums.rate_period_per.${placement.pay_rate}`)}` : t('common.not_specified')}
                          </Typography>
                        </Stack>
                      </Grid>

                      {tenantType === TenantType.Recruiter && (
                        <>
                          <Grid xs={12} md={6}>
                            <Stack spacing={1}>
                              <Typography variant="body2" color="text.secondary">
                                {t('jobs.detail.placements.form.bill_rate')}
                              </Typography>
                              <Typography variant="body1">
                                {placement.bill_rate ? `${placement.bill_rate} ${placement.currency?.code || ''}` : t('common.not_specified')}
                              </Typography>
                            </Stack>
                          </Grid>

                          <Grid xs={12} md={6}>
                            <Stack spacing={1}>
                              <Typography variant="body2" color="text.secondary">
                                {t('jobs.detail.placements.form.margin')}
                              </Typography>
                              <Typography variant="body1">
                                {placement.margin ? `${placement.margin} ${placement.currency?.code || ''}` : t('common.not_specified')}
                              </Typography>
                            </Stack>
                          </Grid>

                          <Grid xs={12} md={6}>
                            <Stack spacing={1}>
                              <Typography variant="body2" color="text.secondary">
                                {t('jobs.detail.placements.form.total_margin')}
                              </Typography>
                              <Typography variant="body1">
                                {placement.total_margin ? `${placement.total_margin} ${placement.currency?.code || ''}` : t('common.not_specified')}
                              </Typography>
                            </Stack>
                          </Grid>

                          <Grid xs={12} md={6}>
                            <Stack spacing={1}>
                              <Typography variant="body2" color="text.secondary">
                                {t('jobs.detail.placements.form.profit_calculation_type')}
                              </Typography>
                              <Typography variant="body1">
                                {placement.profit_calculation_type ? `${t(`enums.profit_calculation_type.${placement.profit_calculation_type}`)}` : t('common.not_specified')}
                              </Typography>
                            </Stack>
                          </Grid>

                          <Grid xs={12} md={6}>
                            <Stack spacing={1}>
                              <Typography variant="body2" color="text.secondary">
                                {t('jobs.detail.placements.form.on_costs')}
                              </Typography>
                              <Typography variant="body1">
                                {placement.on_costs ? `${placement.on_costs} ${placement.currency?.code || ''}` : t('common.not_specified')}
                              </Typography>
                            </Stack>
                          </Grid>

                          <Grid xs={12} md={6}>
                            <Stack spacing={1}>
                              <Typography variant="body2" color="text.secondary">
                                {t('jobs.detail.placements.form.is_prorated_fee')}
                              </Typography>
                              <Typography variant="body1">
                                {placement.is_prorated_fee ? `${t('common.yes')} (${placement.proration_percentage}%)` : t('common.no')}
                              </Typography>
                            </Stack>
                          </Grid>

                          <Grid xs={12} md={6}>
                            <Stack spacing={1}>
                              <Typography variant="body2" color="text.secondary">
                                {t('jobs.detail.placements.form.billable_days')}
                              </Typography>
                              <Typography variant="body1">
                                {placement.billable_days ? `${placement.billable_days} days` : t('common.not_specified')}
                              </Typography>
                            </Stack>
                          </Grid>

                          <Grid xs={12} md={6}>
                            <Stack spacing={1}>
                              <Typography variant="body2" color="text.secondary">
                                {t('jobs.detail.placements.form.placement_fee')}
                              </Typography>
                              <Typography variant="body1">
                                {placement.placement_fee_value || t('common.not_specified')}
                                {placement.placement_fee_type &&
                                  <>
                                    {
                                      placement.placement_fee_type === FeeType.PERCENTAGE
                                        ? '%'
                                        : ` ${placement.currency?.code || ''}`
                                    }
                                  </>
                                }
                              </Typography>
                            </Stack>
                          </Grid>

                        </>
                      )}
                    </Grid>
                  </CardContent>
                </Card>

                {/* Billing Information Card */}
                {tenantType === TenantType.Recruiter && (
                  <Card>
                    <CardHeader title={t('jobs.detail.placements.form.billing_info')} />
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid xs={12} md={6}>
                          <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                              {t('jobs.detail.placements.form.billing_contact_name')}
                            </Typography>

                            {placement.billing_contact ? (
                              <Link
                                component="button"
                                variant="body1"
                                onClick={navigateToBillingContact}
                                sx={{ textAlign: 'left' }}
                              >
                                {`${placement.billing_contact.first_name || ''} ${placement.billing_contact.last_name || ''}`.trim() || t('common.not_specified')}
                              </Link>
                            ) : (
                              <Typography variant="body1">
                                {placement.billing_contact_first_name && placement.billing_contact_last_name
                                  ? `${placement.billing_contact_first_name} ${placement.billing_contact_last_name}`
                                  : t('common.not_specified')}
                              </Typography>
                            )}

                          </Stack>
                        </Grid>

                        <Grid xs={12} md={6}>
                          <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                              {t('jobs.detail.placements.form.billing_contact_email')}
                            </Typography>
                            <Typography variant="body1">
                              {placement.billing_contact_email || t('common.not_specified')}
                            </Typography>
                          </Stack>
                        </Grid>

                        <Grid xs={12} md={6}>
                          <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                              {t('jobs.detail.placements.form.billing_invoice_number')}
                            </Typography>
                            <Typography variant="body1">
                              {placement.billing_invoice_number || t('common.not_specified')}
                            </Typography>
                          </Stack>
                        </Grid>

                        <Grid xs={12} md={6}>
                          <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                              {t('jobs.detail.placements.form.billing_po_number')}
                            </Typography>
                            <Typography variant="body1">
                              {placement.billing_po_number || t('common.not_specified')}
                            </Typography>
                          </Stack>
                        </Grid>

                        <Grid xs={12} md={6}>
                          <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                              {t('jobs.detail.placements.form.payment_terms')}
                            </Typography>
                            <Typography variant="body1">
                              {placement.payment_terms || t('common.not_specified')}
                            </Typography>
                          </Stack>
                        </Grid>

                        <Grid xs={12} md={6}>
                          <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                              {t('jobs.detail.placements.form.payment_frequency')}
                            </Typography>
                            <Typography variant="body1">
                              {placement.payment_frequency ? t(`enums.payment_frequency.${placement.payment_frequency}`) : t('common.not_specified')}
                            </Typography>
                          </Stack>
                        </Grid>

                        <Grid xs={12} md={6}>
                          <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                              {t('jobs.detail.placements.form.send_invoice_on')}
                            </Typography>
                            <Typography variant="body1">
                              {placement.send_invoice_on ? fDate(placement.send_invoice_on) : t('common.not_specified')}
                            </Typography>
                          </Stack>
                        </Grid>

                        <Grid xs={12} md={6}>
                          <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                              {t('jobs.detail.placements.form.currency')}
                            </Typography>
                            <Typography variant="body1">
                              {placement.currency ? placement.currency.code : t('common.not_specified')}
                            </Typography>
                          </Stack>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                )}

                {/* Custom Fields */}
                {customFields.hasCustomFields && (
                  <Card>
                    <CardHeader title={t('common.custom_fields')} />
                    <CardContent>
                      <Grid container spacing={2}>
                        {customFields.getFields().map((field) => (
                          <Grid xs={12} md={6} key={field.label}>
                            <Stack spacing={1}>
                              <Typography variant="body2" color="text.secondary">
                                {field.label}
                              </Typography>
                              <Typography variant="body1">
                                {field.value || t('common.not_specified')}
                              </Typography>
                            </Stack>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                )}

              </Stack>
            </Grid>

            <Grid xs={12} md={4}>
              <Stack spacing={3}>
                {/* Placement Information Card */}
                <Card>
                  <CardHeader title={t('jobs.detail.placements.detail.placement_info')} />
                  <CardContent>
                    <Stack spacing={2}>
                      <Stack spacing={1}>
                        <Typography variant="body2" color="text.secondary">
                          {t('jobs.detail.placements.form.placed_on')}
                        </Typography>
                        <Typography variant="body1">
                          {fDate(placement.placed_at)}
                        </Typography>
                      </Stack>

                      <Stack spacing={1}>
                        <Typography variant="body2" color="text.secondary">
                          {t('jobs.detail.placements.form.duration')}
                        </Typography>
                        <Typography variant="body1">
                          {placement.duration_days || t('common.not_specified')}
                        </Typography>
                      </Stack>

                      <Stack spacing={1}>
                        <Typography variant="body2" color="text.secondary">
                          {t('jobs.detail.placements.form.hours_per_day')}
                        </Typography>
                        <Typography variant="body1">
                          {placement.hours_per_day || t('common.not_specified')}
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Grid>

          </Grid>
        )
      }

      {
        currentTab === PlacementTab.DOCUMENTS && (
          <Box sx={{ px: 2, pb: 2 }}>
            <PlacementDocumentsView placementId={placement_id} />
          </Box>
        )
      }

      <ConfirmDialog
        open={confirmDelete.value}
        onClose={confirmDelete.onFalse}
        title={t('jobs.detail.placements.remove.title')}
        content={t('jobs.detail.placements.remove.confirm')}
        action={
          <LoadingButton variant="contained" color="error" onClick={onDeletePlacement} loading={isDeletingPlacement}>
            {t('common.delete')}
          </LoadingButton>
        }
      />
    </>
  );
}